<template>
  <v-container>
    <!-- ContentHeader.vue -->
    <router-view></router-view>
    <foldable-card
      non-foldable
      title="Add New Owner"
      style="border-top-width: 8px"
    >
      <v-card-actions class="pa-sm-0">
        <v-container class="text-right pa-sm-0 mr-sm-4">
          <v-btn
            text
            :to="navigateToOnDismiss"
            exact
            class="edit-button"
            :block="$vuetify.breakpoint.xsOnly"
            @click="$emit('cancel-clicked')"
          >
            <span>Cancel</span>
          </v-btn>
          <v-btn
            v-if="controls.editMode"
            color="primary"
            class="save-changes-button"
            :block="$vuetify.breakpoint.xsOnly"
            @click="saveChanges"
            :disabled="!formValid"
          >
            Save Changes
          </v-btn>
        </v-container>
      </v-card-actions>
      <v-card-text>
        <v-form v-model="formValid" ref="form">
          <v-container pa-0>
            <v-row no-gutters>
              <v-col cols="12" lg="3">
                <div class="information-container-title">
                  Is this owner a business?
                </div>
              </v-col>
              <v-col cols="12" lg="3">
                <v-checkbox
                  :disabled="!controls.editMode"
                  :label="vm.thisIsABusiness ? 'Yes' : 'No'"
                  class="mt-0 pt-0"
                  v-model="vm.thisIsABusiness"
                />
              </v-col>
            </v-row>
            <v-divider class="my-8"></v-divider>
            <v-row no-gutters v-if="vm.thisIsABusiness">
              <v-col cols="12" lg="3">
                <div class="information-container-title">
                  Legal Business Name
                </div>
              </v-col>
              <v-col cols="12" lg="7">
                <v-text-field
                  filled
                  dense
                  v-model="vm.businessName"
                  label="Company*"
                  :disabled="!controls.editMode"
                  :rules="[rules.condRequired('Company', vm.thisIsABusiness)]"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row no-gutters v-if="!vm.thisIsABusiness">
              <v-col cols="12" lg="3">
                <div class="information-container-title">
                  Owner Information
                </div>
              </v-col>
              <v-col cols="12" lg="9">
                <v-row no-gutters>
                  <v-col cols="12" lg="7">
                    <v-text-field
                      filled
                      dense
                      class="first-name"
                      v-model="vm.firstName"
                      label="First Name*"
                      :disabled="!controls.editMode"
                      :rules="[
                        rules.condRequired('First Name', !vm.thisIsABusiness)
                      ]"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col cols="12" lg="7">
                    <v-text-field
                      filled
                      dense
                      v-model="vm.lastName"
                      label="Last Name*"
                      :disabled="!controls.editMode"
                      :rules="[
                        rules.condRequired('Last Name', !vm.thisIsABusiness)
                      ]"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col cols="12" lg="7">
                    <date-field
                      v-model="vm.dob"
                      :maxDate="this.getNow()"
                      :label="
                        'Date of Birth' + (controls.dobRequired ? '*' : '')
                      "
                      :rules="[
                        rules.condRequired(
                          'Date of Birth',
                          controls.dobRequired
                        )
                      ]"
                      :disabled="!controls.editMode"
                    >
                    </date-field>
                  </v-col>
                  <v-col cols="12" lg="7">
                    <v-autocomplete
                      :items="['Male', 'Female', 'Unspecified']"
                      filled
                      dense
                      v-model="vm.gender"
                      label="Gender"
                      :disabled="!controls.editMode"
                    ></v-autocomplete>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <v-divider class="my-8"></v-divider>
            <v-row no-gutters>
              <v-col cols="12" lg="3">
                <div class="information-container-title">
                  Address Information
                </div>
              </v-col>
              <v-col cols="12" lg="9">
                <v-row no-gutters>
                  <v-col cols="12" lg="7">
                    <v-text-field
                      filled
                      dense
                      v-model="vm.street1"
                      label="Billing Address 1"
                      :disabled="!controls.editMode"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col cols="12" lg="7">
                    <v-text-field
                      filled
                      dense
                      v-model="vm.street2"
                      label="Suite, APT (optional)"
                      :disabled="!controls.editMode"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col cols="12" sm="8" lg="4">
                    <v-text-field
                      filled
                      dense
                      class="mr-sm-4"
                      v-model="vm.city"
                      label="City"
                      :disabled="!controls.editMode"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="4" lg="3">
                    <v-autocomplete
                      filled
                      dense
                      clearable
                      label="State"
                      :items="states"
                      v-model="vm.state"
                      item-value="state"
                      item-text="code"
                      class="state-selector"
                      :disabled="!controls.editMode"
                    ></v-autocomplete>
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col cols="12" lg="4">
                    <v-text-field
                      filled
                      dense
                      class="mr-lg-4"
                      v-model="vm.postalCode"
                      label="Zip Code"
                      :rules="[rules.zip]"
                      :disabled="!controls.editMode"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <v-divider class="my-8"></v-divider>
            <v-row no-gutters class="mt-8 mb-2">
              <v-col cols="12" lg="3" md="5">
                Tax Identification
              </v-col>
              <v-col cols="12" lg="5" md="7">
                <v-text-field
                  filled
                  dense
                  v-model="vm.taxId"
                  v-mask="controls.tinMask"
                  :label="
                    (vm.thisIsABusiness ? 'EIN' : 'SSN') +
                      (controls.tinRequired ? '*' : '')
                  "
                  :rules="[
                    rules.nDigits(vm.thisIsABusiness ? 'EIN' : 'SSN', 9),
                    rules.condRequired(
                      vm.thisIsABusiness ? 'EIN' : 'SSN',
                      controls.tinRequired
                    )
                  ]"
                  :disabled="!controls.editMode"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-divider class="my-8"></v-divider>
            <v-row no-gutters>
              <v-col cols="12" lg="3">
                <div class="information-container-title">
                  National Provider
                </div>
              </v-col>
              <v-col cols="12" lg="9">
                <v-row no-gutters>
                  <v-col cols="12" lg="7">
                    <v-text-field
                      filled
                      dense
                      v-mask="controls.npiMask"
                      v-model="vm.npi"
                      label="National Provider Identifier (NPI)"
                      hint="NPI is a unique ID number for covered healthcare providers."
                      :rules="[rules.nDigits('NPI', 10)]"
                      :disabled="!controls.editMode"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>
    </foldable-card>
  </v-container>
</template>

<script>
import FoldableCard from "@/components/FoldableCard";
import { datePickerNow } from "@/services/util";
import DateField from "@/components/DateField";
import * as rules from "@/services/rules";
import { get, post } from "@/services/request";
import { clientConfigAPI, ownersAPI, statesAPI } from "@/services/api";
import { mask } from "vue-the-mask";

export default {
  name: "AddOwner",
  props: {
    ownerId: {
      type: Number
    },
    clientId: {
      //If exists, used to load client-specific configuration
      type: Number,
      default: undefined
    },
    navigateToOnDismiss: {
      //Includes canceling and saving
      type: Object //router location object like {name: 'account', hash: '#owners'}
    }
  },
  data() {
    return {
      vm: {},
      states: [],
      controls: {
        deleteConfirmationDialogOpen: false,
        npiMask: "##########",
        tinMask: "#########",
        showSsn: false,
        menu: null,
        editMode: true,
        tinRequired: false,
        dobRequired: false
      },
      rules,
      formValid: false
    };
  },
  watch: {
    "vm.thisIsABusiness": {
      handler() {
        this.$nextTick(() => {
          this.validate();
        });
      }
    }
  },
  components: {
    "foldable-card": FoldableCard,
    "date-field": DateField
  },
  directives: {
    mask
  },
  created() {
    this.loadStates();
    this.loadConfig();
  },
  methods: {
    getNow: datePickerNow,
    loadStates() {
      get(statesAPI).then(response => {
        if (response && response.data) {
          this.states = response.data;
        }
      });
    },
    loadConfig() {
      if (this.clientId) {
        get(clientConfigAPI.getUrl(this.clientId)).then(response => {
          if (response.data.requirementMap) {
            if (response.data.requirementMap.OWNER_TIN) {
              this.controls.tinRequired =
                response.data.requirementMap.OWNER_TIN;
            }
            if (response.data.requirementMap.OWNER_DOB) {
              this.controls.dobRequired =
                response.data.requirementMap.OWNER_DOB;
            }
            this.$nextTick(() => {
              this.validate();
            });
          }
        });
      }
    },
    validate() {
      if (this.$refs.form) {
        this.$refs.form.validate();
      }
      this.$emit("form-valid", this.formValid);
    },
    saveChanges() {
      return post(ownersAPI, this.vm).then(response => {
        this.$emit("save-success");
        if (response && this.navigateToOnDismiss) {
          this.$router.push(this.navigateToOnDismiss);
        }
      });
    }
  }
};
</script>

<style lang="sass" scoped>
.v-btn
  text-transform: none
  font-weight: bold
  letter-spacing: unset
  text-indent: unset
  font-size: 1rem

.v-btn.v-btn--text
  font-weight: normal
  color: $subtitle

h1
  font-size: 40px
  line-height: 50px
  color: $darker-grey

h2
  font-size: 35px
  line-height: 44px
  color: $darker-grey

h3
  font-size: 24px
  font-weight: normal
  line-height: 31px
  color: $darker-grey

.back-button.v-btn.v-btn--text
  & ::v-deep > .v-btn__content
    color: $subtitle
    font-size: 16px
    line-height: 20px
    text-align: center
    font-weight: 600
    text-indent: unset
</style>
